import React from "react";
import { useQuery } from "react-query";
import { useDispatch } from "react-redux";

import { adoptableV2Session } from "../auth/adopt-v2-session";
import { updateTokens } from "../auth/slice/auth-reducer";
import { useSession } from "../auth/slice/auth-selectors";
import { useEnv } from "../env/provider";
import { useApi } from "../network/provider";

import { readEvaluation, readRotatedTokens } from "./evaluation";
import {
  sessionAdoptionAttempted,
  shouldForwardToV2,
  subscribeForwarding,
} from "./forwarding";

export const CUTOVER_VARIANT_QUERY_KEY = "cutover-variant";

/** The appconfig feature toggle that carries both the map and the variants. */
const TOGGLE_NAME = "family_web_v2_routes";

export type CutoverVariantResult = {
  /** False while the answer is still unknown. Nothing may forward yet. */
  resolved: boolean;
  /** Whether this visitor's owned routes belong to the new app. */
  allowed: boolean;
  /** Whether the answer is for someone with no session here — not signed in,
   *  and not a family whose session is still being adopted. */
  anonymous: boolean;
};

/**
 * Which side of the A/B split this visitor is on.
 *
 * Anonymous visitors never reach the network: the evaluate route is
 * session-authenticated, pre-login screens are always-on, and asking would
 * only be a 401 to fail closed on.
 *
 * Signed-in visitors are evaluated once per page load. appconfig rolls the
 * variant on the first call and pins it per user, so the answer is stable for
 * a family across visits and there is nothing to re-read within one.
 */
export function useCutoverVariant(): CutoverVariantResult {
  const api = useApi();
  const dispatch = useDispatch();
  const { appconfig } = useEnv().services;
  const session = useSession();

  const tokens =
    session.accessToken && session.refreshToken
      ? {
          accessToken: session.accessToken,
          refreshToken: session.refreshToken,
        }
      : null;

  const signedIn = tokens !== null;

  // A family signed in on the new app is adopted into this one by an effect
  // that runs below this gate, so for the first render they still look
  // anonymous — and anonymous means "always forward". Treating that frame as
  // anonymous would send a v1-arm family to the new app before the adoption
  // had a chance to say who they are, and a forward is a page load there is
  // no coming back from.
  //
  // Read once, at mount, and released by an effect that always runs. Re-read
  // every render it would never end: a key that appears later (another tab
  // signing into the new app) has no adoption coming for it in this tab, and
  // the gate would hold for the rest of the page load.
  const [sawSessionToAdopt] = React.useState(
    () => adoptableV2Session({}) !== null,
  );
  const adoptionDone = React.useSyncExternalStore(
    subscribeForwarding,
    sessionAdoptionAttempted,
    sessionAdoptionAttempted,
  );
  const settling = !signedIn && sawSessionToAdopt && !adoptionDone;

  const { status, data } = useQuery(
    [CUTOVER_VARIANT_QUERY_KEY, session.userId],
    () =>
      api.post<unknown>("/v1/feature-toggle/evaluate", {
        baseUrl: appconfig,
        body: { name: TOGGLE_NAME },
        // The gate renders above SessionGate, which is what normally arms the
        // shared client with the session's tokens. Waiting for a component
        // this one decides whether to render would deadlock the evaluation
        // into a 401, so the call carries the session itself.
        headers: tokens
          ? {
              Authorization: `Bearer ${tokens.accessToken}`,
              "Refresh-Token": tokens.refreshToken,
            }
          : {},
        // And for the same reason it has to honour a rotation itself:
        // SessionGate's interceptor is not mounted yet, and a rotated pair
        // dropped here 401s on the very next request and takes both apps'
        // sessions with it. Only the tokens are touched — a 401 is left to
        // fail the query and render, never to clear a session from up here.
        onResponse: (response) => {
          const rotated = readRotatedTokens((name) =>
            response.headers.get(name),
          );

          if (rotated) {
            dispatch(updateTokens(rotated));
          }
        },
      }),
    {
      enabled: signedIn,
      staleTime: Infinity,
      cacheTime: Infinity,
      retry: false,
    },
  );

  const evaluation = React.useMemo(
    () => readEvaluation(status, data),
    [status, data],
  );

  const decision = shouldForwardToV2({
    signedIn: signedIn || settling,
    evaluation: settling ? { status: "pending" } : evaluation,
  });

  const anonymous = !signedIn && !settling;

  return React.useMemo(
    () => ({
      resolved: decision !== "hold",
      allowed: decision === "forward",
      anonymous,
    }),
    [anonymous, decision],
  );
}
